import { createSelector } from 'reselect';
import { DELIVERY_STATE, ORDER_STATE, ORDER_TYPE, SERVICE_TYPE } from '../../../constant.js';

/**
 * Get just the list of orders
 * @param {*} state
 * @returns {OrderState.Order[]}
 */
const getOrders = (state) => state.order.orders;

export const isUpcomingOrder = (order) => {
  const { deliveryState, state, type } = order.attributes;
  if (state === ORDER_STATE.REFUNDED || state === ORDER_STATE.UNAUTHORIZED) {
    return false;
  }
  if (type === ORDER_TYPE.PICKUP) {
    const { estDeliveryEndAt } = order.attributes;
    return Date.now() - new Date(estDeliveryEndAt) <= 2 * 3600 * 1000; // is less than two hours
  }
  return !(deliveryState === DELIVERY_STATE.DELIVERED || deliveryState === DELIVERY_STATE.MISSED);
};

export const selectUpcomingOrders = createSelector(getOrders, (orders) => {
  const upcommingOrders = Object.values(orders).filter((order) => isUpcomingOrder(order));
  return [...upcommingOrders];
});

export const selectPastOrders = createSelector(getOrders, (orders) => {
  return Object.values(orders).filter((order) => !isUpcomingOrder(order));
});

/**
 * @function
 * Get the complete order state module
 * @returns {OrderState.State}
 */
export const selectOrder = createSelector(
  (state) => state.order,
  (order) => order
);

export const orderTypeToServiceType = (orderType) => {
  const key = Object.keys(ORDER_TYPE).find((key) => {
    return ORDER_TYPE[key] === orderType;
  });
  return SERVICE_TYPE[key];
};

export const getOrderAddress = (order, meals) => {
  const {
    attributes: { type },
    relationships: {
      address: { data: address },
      lineItems: { data: lineItems },
    },
  } = order;
  if (type === ORDER_TYPE.SHUTTLE || type === ORDER_TYPE.DELIVERY) {
    return {
      address: [
        address.attributes.address1,
        address.attributes.city,
        address.attributes.state,
      ].join(', '),
      location: {
        lat: address.attributes.latitude,
        lng: address.attributes.longitude,
      },
    };
  }
  const meal = meals.find((meal) => meal.id === lineItems[0].attributes.mealInstanceId);
  if (!meal) {
    return {
      address: 'restaurant',
    };
  }
  const {
    attributes: { name },
    relationships: {
      address: { data: restAddress },
    },
  } = meal.relationships.restaurant.data;
  return {
    restName: name,
    address: [
      restAddress.attributes.address1,
      restAddress.attributes.address2,
      restAddress.attributes.city,
    ]
      .filter((part) => !!part)
      .join(', '),
    location: {
      lat: restAddress.attributes.latitude,
      lng: restAddress.attributes.longitude,
    },
  };
};

export const mapLineItemsToMeals = ({ lineItems, meals, options }) => {
  return lineItems.map((item) => {
    const {
      attributes: { quantity: mealQuantity, mealInstanceId },
      relationships: {
        itemCustomizations: { data: itemCustomizations },
      },
    } = item;
    const meal = meals[mealInstanceId];
    const customizationOptions = itemCustomizations.reduce((acc, itemCustomization) => {
      const {
        attributes: { quantity, customizedOptionId },
      } = itemCustomization;
      const customizationOption = options[customizedOptionId];
      return {
        ...acc,
        ...{
          [customizedOptionId]: {
            ...customizationOption,
            quantity,
          },
        },
      };
    }, {});
    return {
      ...meal,
      quantity: mealQuantity,
      customizationOptions,
    };
  });
};
